import React, { useState, useEffect } from 'react';
import { X, GitBranch, Clock, User, Tag, RotateCcw, Eye, GitCompare, Download } from 'lucide-react';

const VersionsModal = ({ onClose, workflowId, onRestoreVersion }) => {
  const [versions, setVersions] = useState([]);
  const [selectedVersions, setSelectedVersions] = useState([]);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'compare'
  const [loading, setLoading] = useState(false);

  const sampleVersions = [
    {
      id: 'v1.5.0',
      version: '1.5.0',
      description: 'Added AI candidate screening with improved accuracy',
      author: 'John Smith',
      timestamp: '2024-01-07T14:30:00Z',
      changes: ['Added new AI node for resume parsing', 'Updated decision logic', 'Fixed notification formatting'],
      nodeCount: 12,
      isActive: true,
      tags: ['production', 'stable'],
      nodes: [
        { id: '1', type: 'start', position: { x: 100, y: 50 }, data: { label: 'Start' } },
        { id: '2', type: 'ai', position: { x: 100, y: 150 }, data: { label: 'Parse Resume' } },
        { id: '3', type: 'decision', position: { x: 100, y: 250 }, data: { label: 'Skills Match?' } },
      ],
      edges: [
        { id: 'e1-2', source: '1', target: '2' },
        { id: 'e2-3', source: '2', target: '3' },
      ],
    },
    {
      id: 'v1.4.2',
      version: '1.4.2',
      description: 'Hotfix for notification system reliability',
      author: 'Sarah Johnson',
      timestamp: '2024-01-05T11:15:00Z',
      changes: ['Fixed email notification delivery', 'Updated error handling'],
      nodeCount: 11,
      isActive: false,
      tags: ['hotfix'],
      nodes: [],
      edges: [],
    },
    {
      id: 'v1.4.0',
      version: '1.4.0',
      description: 'Major update with database integration and loop support',
      author: 'Mike Chen',
      timestamp: '2024-01-03T16:45:00Z',
      changes: ['Added database operations', 'Implemented loop nodes', 'Enhanced error handling', 'Performance improvements'],
      nodeCount: 10,
      isActive: false,
      tags: ['feature', 'database'],
      nodes: [],
      edges: [],
    },
    {
      id: 'v1.3.1',
      version: '1.3.1',
      description: 'Bug fixes and UI improvements',
      author: 'Lisa Davis',
      timestamp: '2024-01-01T09:30:00Z',
      changes: ['Fixed node positioning', 'Improved connection handles', 'Updated styling'],
      nodeCount: 8,
      isActive: false,
      tags: ['bugfix', 'ui'],
      nodes: [],
      edges: [],
    },
    {
      id: 'v1.3.0',
      version: '1.3.0',
      description: 'Initial workflow automation release',
      author: 'John Smith',
      timestamp: '2023-12-28T14:20:00Z',
      changes: ['Basic workflow functionality', 'Node system implementation', 'Initial API integration'],
      nodeCount: 6,
      isActive: false,
      tags: ['initial', 'beta'],
      nodes: [],
      edges: [],
    },
  ];

  useEffect(() => {
    setVersions(sampleVersions);
  }, [workflowId]);

  const handleCompareVersions = () => {
    if (selectedVersions.length === 2) {
      setViewMode('compare');
    }
  };

  const handleRestoreVersion = (version) => {
    onRestoreVersion(version);
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const getTagColor = (tag) => {
    const colors = {
      production: 'bg-green-100 text-green-800',
      stable: 'bg-blue-100 text-blue-800',
      hotfix: 'bg-red-100 text-red-800',
      feature: 'bg-purple-100 text-purple-800',
      database: 'bg-yellow-100 text-yellow-800',
      bugfix: 'bg-orange-100 text-orange-800',
      ui: 'bg-pink-100 text-pink-800',
      initial: 'bg-gray-100 text-gray-800',
      beta: 'bg-indigo-100 text-indigo-800',
    };
    return colors[tag] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Version History</h2>
              <p className="text-gray-600">View, compare, and restore workflow versions</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-2 rounded-lg transition-colors ${
                    viewMode === 'list' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'
                  }`}
                >
                  List View
                </button>
                <button
                  onClick={() => setViewMode('compare')}
                  disabled={selectedVersions.length !== 2}
                  className={`px-3 py-2 rounded-lg transition-colors ${
                    viewMode === 'compare' ? 'bg-blue-100 text-blue-700' : 
                    selectedVersions.length === 2 ? 'hover:bg-gray-100' : 'opacity-50 cursor-not-allowed'
                  }`}
                >
                  Compare ({selectedVersions.length}/2)
                </button>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
          {viewMode === 'list' ? (
            // List View
            <div className="p-6">
              <div className="space-y-4">
                {versions.map((version) => (
                  <div
                    key={version.id}
                    className={`border border-gray-200 rounded-lg p-6 ${
                      version.isActive ? 'border-blue-300 bg-blue-50' : 'bg-white'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4 flex-1">
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={selectedVersions.includes(version.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                if (selectedVersions.length < 2) {
                                  setSelectedVersions([...selectedVersions, version.id]);
                                }
                              } else {
                                setSelectedVersions(selectedVersions.filter(id => id !== version.id));
                              }
                            }}
                            className="text-blue-600"
                          />
                          <GitBranch className={`h-5 w-5 ${version.isActive ? 'text-blue-600' : 'text-gray-400'}`} />
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">
                              Version {version.version}
                              {version.isActive && (
                                <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                                  ACTIVE
                                </span>
                              )}
                            </h3>
                            <div className="flex space-x-1">
                              {version.tags.map((tag) => (
                                <span
                                  key={tag}
                                  className={`px-2 py-1 text-xs font-medium rounded ${getTagColor(tag)}`}
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                          
                          <p className="text-gray-600 mb-3">{version.description}</p>
                          
                          <div className="flex items-center space-x-6 text-sm text-gray-500 mb-3">
                            <div className="flex items-center space-x-1">
                              <User className="h-4 w-4" />
                              <span>{version.author}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Clock className="h-4 w-4" />
                              <span>{formatDate(version.timestamp)}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Tag className="h-4 w-4" />
                              <span>{version.nodeCount} nodes</span>
                            </div>
                          </div>
                          
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-1">Changes:</h4>
                            <ul className="text-sm text-gray-600 space-y-1">
                              {version.changes.map((change, index) => (
                                <li key={index} className="flex items-start space-x-2">
                                  <span className="text-green-600 mt-1">•</span>
                                  <span>{change}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="View Version"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        
                        <button
                          className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Export Version"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                        
                        {!version.isActive && (
                          <button
                            onClick={() => handleRestoreVersion(version)}
                            className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
                          >
                            <RotateCcw className="h-4 w-4" />
                            <span>Restore</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            // Compare View
            <div className="p-6">
              <div className="grid grid-cols-2 gap-6">
                {selectedVersions.map((versionId) => {
                  const version = versions.find(v => v.id === versionId);
                  return (
                    <div key={versionId} className="border border-gray-200 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        Version {version.version}
                      </h3>
                      
                      <div className="space-y-3 text-sm">
                        <div>
                          <span className="font-medium">Author:</span> {version.author}
                        </div>
                        <div>
                          <span className="font-medium">Date:</span> {formatDate(version.timestamp)}
                        </div>
                        <div>
                          <span className="font-medium">Nodes:</span> {version.nodeCount}
                        </div>
                        <div>
                          <span className="font-medium">Description:</span>
                          <p className="mt-1 text-gray-600">{version.description}</p>
                        </div>
                        <div>
                          <span className="font-medium">Changes:</span>
                          <ul className="mt-1 space-y-1">
                            {version.changes.map((change, index) => (
                              <li key={index} className="flex items-start space-x-2">
                                <span className="text-green-600 mt-1">•</span>
                                <span className="text-gray-600">{change}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h4 className="font-medium text-yellow-800 mb-2">Comparison Summary</h4>
                <div className="text-sm text-yellow-700">
                  <p>Detailed diff view and side-by-side workflow comparison coming soon.</p>
                  <p>Current view shows basic version information for selected versions.</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500">
              {versions.length} versions available
            </div>
            <div className="flex space-x-3">
              {selectedVersions.length === 2 && (
                <button
                  onClick={handleCompareVersions}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-2"
                >
                  <GitCompare className="h-4 w-4" />
                  <span>Compare Selected</span>
                </button>
              )}
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VersionsModal;